import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    BookOpen,
    ToggleLeft,
    AlertTriangle,
    Play,
    Shield,
    ExternalLink,
    Clock,
    Database,
} from 'lucide-react';
import { fetchRunbooks, fetchFeatureFlags } from '../data/adapters';
import { Runbook, FeatureFlag } from '../types';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const OperationsControlTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [runbooks, setRunbooks] = useState<Runbook[]>([]);
    const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const [runbooksData, flagsData] = await Promise.all([
                    fetchRunbooks(isDemo).catch(() => []),
                    fetchFeatureFlags(isDemo).catch(() => []),
                ]);
                setRunbooks(runbooksData);
                setFeatureFlags(flagsData);
                if (runbooksData.length === 0 && flagsData.length === 0) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    const handleFeatureFlagToggle = async (flagId: string, currentEnabled: boolean) => {
        // In demo mode, this would be a no-op with success toast
        // In live mode, this would call the API
        console.log(`Toggling feature flag ${flagId} to ${!currentEnabled}`);
        // For now, just update local state
        setFeatureFlags(prev =>
            prev.map(flag => (flag.id === flagId ? { ...flag, enabled: !currentEnabled } : flag))
        );
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Operations Control Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    const renderNoDataPlaceholder = (title: string) => (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Database size={32} className="mb-2 opacity-50" />
            <p className="text-sm">{title}</p>
            <p className="text-xs mt-1">No data available</p>
        </div>
    );

    if (noData) {
        return (
            <div className="space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                            <div className="flex items-center space-x-3 mb-2">
                                <Play size={20} className="text-green-400" />
                                <span className="text-white font-medium">Safe Restart</span>
                            </div>
                            <p className="text-sm text-slate-400">Restart services safely</p>
                        </button>

                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                            <div className="flex items-center space-x-3 mb-2">
                                <Shield size={20} className="text-blue-400" />
                                <span className="text-white font-medium">Health Check</span>
                            </div>
                            <p className="text-sm text-slate-400">Run system health check</p>
                        </button>

                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                            <div className="flex items-center space-x-3 mb-2">
                                <Clock size={20} className="text-purple-400" />
                                <span className="text-white font-medium">Maintenance</span>
                            </div>
                            <p className="text-sm text-slate-400">Schedule maintenance</p>
                        </button>

                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                            <div className="flex items-center space-x-3 mb-2">
                                <AlertTriangle size={20} className="text-orange-400" />
                                <span className="text-white font-medium">Incident</span>
                            </div>
                            <p className="text-sm text-slate-400">Create incident ticket</p>
                        </button>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Runbooks</h3>
                    {renderNoDataPlaceholder('Runbook data')}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Feature Flags</h3>
                    {renderNoDataPlaceholder('Feature flag data')}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">
                        Incident Response Templates
                    </h3>
                    {renderNoDataPlaceholder('Incident templates')}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">System Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                            <div className="w-3 h-3 bg-slate-500 rounded-full mx-auto mb-2"></div>
                            <div className="text-sm text-slate-400 mb-1">Production</div>
                            <div className="text-lg font-semibold text-slate-500">Unknown</div>
                        </div>

                        <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                            <div className="w-3 h-3 bg-slate-500 rounded-full mx-auto mb-2"></div>
                            <div className="text-sm text-slate-400 mb-1">Staging</div>
                            <div className="text-lg font-semibold text-slate-500">Unknown</div>
                        </div>

                        <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                            <div className="w-3 h-3 bg-slate-500 rounded-full mx-auto mb-2"></div>
                            <div className="text-sm text-slate-400 mb-1">Development</div>
                            <div className="text-lg font-semibold text-slate-500">Unknown</div>
                        </div>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Quick Actions */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                        <div className="flex items-center space-x-3 mb-2">
                            <Play size={20} className="text-green-400" />
                            <span className="text-white font-medium">Safe Restart</span>
                        </div>
                        <p className="text-sm text-slate-400">Restart services safely</p>
                    </button>

                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                        <div className="flex items-center space-x-3 mb-2">
                            <Shield size={20} className="text-blue-400" />
                            <span className="text-white font-medium">Health Check</span>
                        </div>
                        <p className="text-sm text-slate-400">Run system health check</p>
                    </button>

                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                        <div className="flex items-center space-x-3 mb-2">
                            <Clock size={20} className="text-purple-400" />
                            <span className="text-white font-medium">Maintenance</span>
                        </div>
                        <p className="text-sm text-slate-400">Schedule maintenance</p>
                    </button>

                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left">
                        <div className="flex items-center space-x-3 mb-2">
                            <AlertTriangle size={20} className="text-orange-400" />
                            <span className="text-white font-medium">Incident</span>
                        </div>
                        <p className="text-sm text-slate-400">Create incident ticket</p>
                    </button>
                </div>
            </motion.div>

            {/* Runbooks */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="evolut-metric-card p-6"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Runbooks</h3>
                    <div className="flex items-center space-x-2 text-sm text-slate-400">
                        <BookOpen size={16} />
                        <span>Standard operating procedures</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {runbooks.map((runbook, index) => (
                        <motion.div
                            key={runbook.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-slate-600/50 transition-colors"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <h4 className="font-medium text-white">{runbook.title}</h4>
                                <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300`}
                                >
                                    {runbook.category}
                                </span>
                            </div>
                            <p className="text-sm text-slate-400 mb-3 line-clamp-2">
                                {runbook.description}
                            </p>
                            <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>
                                    Updated: {new Date(runbook.lastUpdated).toLocaleDateString()}
                                </span>
                                <button className="text-blue-400 hover:text-blue-300 transition-colors">
                                    <ExternalLink size={14} />
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Feature Flags */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="evolut-metric-card p-6"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Feature Flags</h3>
                    <div className="flex items-center space-x-2 text-sm text-slate-400">
                        <ToggleLeft size={16} />
                        <span>Environment controls</span>
                    </div>
                </div>

                <div className="space-y-4">
                    {featureFlags.map((flag, index) => (
                        <motion.div
                            key={flag.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                        >
                            <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                    <h4 className="font-medium text-white">{flag.name}</h4>
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            flag.enabled
                                                ? 'bg-green-500/20 text-green-400'
                                                : 'bg-red-500/20 text-red-400'
                                        }`}
                                    >
                                        {flag.enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300">
                                        {flag.environment}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-400 mb-2">{flag.description}</p>
                                <div className="text-xs text-slate-500">
                                    Last modified:{' '}
                                    {new Date(flag.lastModified).toLocaleDateString()}
                                </div>
                            </div>

                            <div className="flex items-center space-x-3 ml-4">
                                <button
                                    onClick={() => handleFeatureFlagToggle(flag.id, flag.enabled)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        flag.enabled
                                            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                                            : 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30'
                                    }`}
                                >
                                    {flag.enabled ? 'Disable' : 'Enable'}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Incident Templates */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">
                    Incident Response Templates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <div className="flex items-center space-x-3 mb-3">
                            <AlertTriangle size={20} className="text-red-400" />
                            <h4 className="font-medium text-white">Critical Incident</h4>
                        </div>
                        <p className="text-sm text-slate-400 mb-3">
                            Standard response procedure for critical system failures
                        </p>
                        <button className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30 transition-colors">
                            Use Template
                        </button>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <div className="flex items-center space-x-3 mb-3">
                            <AlertTriangle size={20} className="text-yellow-400" />
                            <h4 className="font-medium text-white">Performance Degradation</h4>
                        </div>
                        <p className="text-sm text-slate-400 mb-3">
                            Response procedure for performance issues
                        </p>
                        <button className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded text-sm hover:bg-yellow-500/30 transition-colors">
                            Use Template
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* System Status */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">System Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                        <div className="w-3 h-3 bg-green-400 rounded-full mx-auto mb-2"></div>
                        <div className="text-sm text-slate-400 mb-1">Production</div>
                        <div className="text-lg font-semibold text-green-400">Healthy</div>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                        <div className="w-3 h-3 bg-yellow-400 rounded-full mx-auto mb-2"></div>
                        <div className="text-sm text-slate-400 mb-1">Staging</div>
                        <div className="text-lg font-semibold text-yellow-400">Degraded</div>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                        <div className="w-3 h-3 bg-green-400 rounded-full mx-auto mb-2"></div>
                        <div className="text-sm text-slate-400 mb-1">Development</div>
                        <div className="text-lg font-semibold text-green-400">Healthy</div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};
